import { Injectable } from '@angular/core';
import { SmartCartApi } from '@app/core/services/api/smart-cart-api.service';
import { Observable } from 'rxjs';

export interface SmartCartWidgetPayload {
  id?: number;
  type: string;
  enabled: boolean;
  order: number;
  usageToken?: string;
  settings: Record<string, unknown>;
  customTemplate?: { enabled: boolean; html: string };
}

export interface SmartCartWidgetResponse extends Omit<SmartCartWidgetPayload, 'id'> {
  id: number;
}

export interface SmartCartListItem {
  id: number;
  name: string;
  description: string | null;
  isLive: boolean;
  version: string;
  createdAt: string;
  updatedAt: string;
  global: Record<string, unknown>;
  header: SmartCartWidgetResponse[];
  body: SmartCartWidgetResponse[];
  footer: SmartCartWidgetResponse[];
}

export interface CreateSmartCartPayload {
  name: string;
  description: string | null;
  version?: string;
  global: Record<string, unknown>;
  header: SmartCartWidgetPayload[];
  body: SmartCartWidgetPayload[];
  footer: SmartCartWidgetPayload[];
}

export interface UpdateSmartCartPayload {
  name?: string;
  description?: string | null;
  version?: string;
  global?: Record<string, unknown>;
  header?: SmartCartWidgetPayload[];
  body?: SmartCartWidgetPayload[];
  footer?: SmartCartWidgetPayload[];
}

export interface StandaloneWidgetItem {
  id: number;
  name: string;
  type: string;
  isLive: boolean;
  usageToken: string;
  settings: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateStandaloneWidgetPayload {
  name: string;
  type: string;
  usageToken?: string;
  settings: Record<string, unknown>;
}

export interface UpdateStandaloneWidgetPayload {
  name?: string;
  settings?: Record<string, unknown>;
}

@Injectable({ providedIn: 'root' })
export class SmartCartService {
  constructor(private smartCartApi: SmartCartApi) {}

  list(): Observable<SmartCartListItem[]> {
    return this.smartCartApi.get(`/smart-cart/admin`);
  }

  getById(id: number): Observable<SmartCartListItem> {
    return this.smartCartApi.get(`/smart-cart/admin/${id}`);
  }

  create(payload: CreateSmartCartPayload): Observable<SmartCartListItem> {
    return this.smartCartApi.post(`/smart-cart/admin`, payload);
  }

  update(id: number, payload: UpdateSmartCartPayload): Observable<SmartCartListItem> {
    return this.smartCartApi.patch(`/smart-cart/admin/${id}`, payload);
  }

  activate(id: number): Observable<SmartCartListItem> {
    return this.smartCartApi.patch(`/smart-cart/admin/${id}/activate`, {});
  }

  deactivate(id: number): Observable<SmartCartListItem> {
    return this.smartCartApi.patch(`/smart-cart/admin/${id}/deactivate`, {});
  }

  delete(id: number): Observable<void> {
    return this.smartCartApi.delete(`/smart-cart/admin/${id}`);
  }

  duplicate(id: number): Observable<SmartCartListItem> {
    return this.smartCartApi.post(`/smart-cart/admin/${id}/duplicate`, {});
  }

  // --- Standalone widgets (PDQ-8058) — a dedicated table, served under /smart-cart/admin/standalone-widgets ---

  listStandaloneWidgets(): Observable<StandaloneWidgetItem[]> {
    return this.smartCartApi.get(`/smart-cart/admin/standalone-widgets`);
  }

  getStandaloneWidget(id: number): Observable<StandaloneWidgetItem> {
    return this.smartCartApi.get(`/smart-cart/admin/standalone-widgets/${id}`);
  }

  createStandaloneWidget(payload: CreateStandaloneWidgetPayload): Observable<StandaloneWidgetItem> {
    return this.smartCartApi.post(`/smart-cart/admin/standalone-widgets`, payload);
  }

  updateStandaloneWidget(id: number, payload: UpdateStandaloneWidgetPayload): Observable<StandaloneWidgetItem> {
    return this.smartCartApi.patch(`/smart-cart/admin/standalone-widgets/${id}`, payload);
  }

  deleteStandaloneWidget(id: number): Observable<void> {
    return this.smartCartApi.delete(`/smart-cart/admin/standalone-widgets/${id}`);
  }

  duplicateStandaloneWidget(id: number): Observable<StandaloneWidgetItem> {
    return this.smartCartApi.post(`/smart-cart/admin/standalone-widgets/${id}/duplicate`, {});
  }

  activateStandaloneWidget(id: number): Observable<StandaloneWidgetItem> {
    return this.smartCartApi.patch(`/smart-cart/admin/standalone-widgets/${id}/activate`, {});
  }

  deactivateStandaloneWidget(id: number): Observable<StandaloneWidgetItem> {
    return this.smartCartApi.patch(`/smart-cart/admin/standalone-widgets/${id}/deactivate`, {});
  }

  uploadLogo(id: number, file: File): Observable<{ url: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.smartCartApi.post(`/smart-cart/admin/${id}/logo`, formData);
  }

  uploadIcon(
    id: number,
    usageToken: string,
    category: 'goals-icons' | 'timer-icons',
    file: File,
  ): Observable<{ url: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('usageToken', usageToken);
    formData.append('category', category);
    return this.smartCartApi.post(`/smart-cart/admin/${id}/icons`, formData);
  }
}
